<template>
  <div class="experiment-comments">
    <div class="header-row">
      <h3>Comments</h3>
      <span class="meta-count">{{ (comments || []).length }} item(s)</span>
    </div>
    <div class="add-comment-form">
      <textarea v-model="newCommentText" placeholder="Write a comment..."></textarea>
      <div class="upload-controls">
        <div class="submit-button-container">
          <button type="button" @click="submitComment">Submit</button>
        </div>
      </div>
    </div>

    <ul>
      <li
        v-for="comment in paginatedComments"
        :key="comment.id"
        class="comment-item"
      >
        <div class="comment-top">
          <div class="comment-meta">
            Created by {{ userNames[comment.author_id] || 'Loading...' }}
            at {{ formatDate(comment.timestamp) }}
          </div>
          <div class="comment-actions">
            <button
              class="edit-btn"
              @click="editComment(comment)"
              v-if="editingCommentId !== comment.id"
            >
              <i class="bi bi-pencil-square"></i>
            </button>
            <button class="remove-btn" @click="handleRemoveComment(comment.id)">
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>
        <div class="comment-content">
          <div v-if="editingCommentId !== comment.id">
            <div class="comment-text">{{ comment.text }}</div>
          </div>
          <div v-else>
            <textarea v-model="editingText" class="edit-textarea"></textarea>
            <button @click="saveEdit(comment.id)" class="icon-btn save-btn">
              <i class="bi bi-check-square-fill"> Save</i>
            </button>
            <button @click="cancelEdit()" class="icon-btn cancel-btn">
              <i class="bi bi-x-square-fill"> Cancel</i>
            </button>
          </div>
          <ul class="comment-files">
            <li
              v-for="file in getFilesByCommentId(comment.id)"
              :key="file.id"
            >
              File: {{ getFileName(file.path) }}
            </li>
          </ul>
        </div>
      </li>
    </ul>

    <!-- Pagination Controls -->
    <div class="pagination" v-if="totalPages > 1">
      <button
        v-for="page in visiblePageNumbers"
        :key="page"
        @click="setPage(page)"
        :class="{ active: page === currentPage }"
      >
        {{ page }}
      </button>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import { API_ENDPOINTS } from '@/services/config';
import { mapGetters } from 'vuex';
import { useToast } from 'vue-toastification';
import { confirmDelete } from '@/utils/confirmDelete';

export default {
  name: 'EntityComments',
  props: {
    comments: Array,
    users: Array,
    associativeId: Number,
    associativeType: String,
    files: Array,
    entityId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      newCommentText: '',
      editingCommentId: null,
      editingText: '',
      userNames: {},
      // pagination state
      currentPage: 1,
      commentsPerPage: 5,
    };
  },
  computed: {
    ...mapGetters(['getLoggedInUser']),

    // total number of pages
    totalPages() {
      return Math.ceil(this.comments.length / this.commentsPerPage);
    },
    // slice out only the comments for the current page
    paginatedComments() {
      const start = (this.currentPage - 1) * this.commentsPerPage;
      return this.comments.slice(start, start + this.commentsPerPage);
    },
    // generate a range of visible page buttons
    visiblePageNumbers() {
      const range = 5;
      let start = Math.max(1, this.currentPage - Math.floor(range / 2));
      let end = start + range - 1;
      if (end > this.totalPages) {
        end = this.totalPages;
        start = Math.max(1, end - range + 1);
      }
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  emits: ['addComment', 'removeComment', 'updateComment'],
  watch: {
    comments: {
      immediate: true,
      handler(newComments) {
        newComments.forEach((c) => this.fetchUserName(c.author_id));
        // If comments change, reset to page 1
        this.currentPage = 1;
      },
    },
  },
  methods: {
    async fetchUserName(userId) {
      if (!this.userNames[userId]) {
        try {
          const user = await apiService.fetchResources(
            API_ENDPOINTS.user(userId)
          );
          this.userNames[userId] = user.name;
        } catch (error) {
          console.error('Error fetching user:', error);
          this.userNames[userId] = 'Unknown user';
        }
      }
    },
    formatDate(dateString) {
      console.log('Formatting date:', dateString);
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    submitComment() {
      const loggedInUser = this.$store.state.loggedInUser;
      if (this.newCommentText.trim() && loggedInUser) {
        this.$emit(
          'addComment',
          this.newCommentText,
          loggedInUser.id
        );
        this.newCommentText = '';
      } else {
        this.toast.error('Comment or user empty.');
      }
    },
    async handleRemoveComment(commentId) {
      const isConfirmed = await confirmDelete(
        'Are you sure you want to delete this Comment?'
      );
      if (!isConfirmed) return;
      this.$emit('removeComment', commentId);
    },
    getFilesByCommentId(commentId) {
      return this.files.filter(
        (file) =>
          file.associative_id === commentId &&
          file.associative_type === 'comment'
      );
    },
    editComment(comment) {
      this.editingCommentId = comment.id;
      this.editingText = comment.text;
    },
    saveEdit(commentId) {
      const comment = this.comments.find((c) => c.id === commentId);
      if (comment) {
        comment.text = this.editingText;
        this.$emit(
          'updateComment',
          commentId,
          this.editingText,
          this.entityId
        );
      }
      this.editingCommentId = null;
      this.editingText = '';
    },
    cancelEdit() {
      this.editingCommentId = null;
      this.editingText = '';
    },
    // switch to a different page
    setPage(page) {
      this.currentPage = page;
    },
  },
};
</script>
<style scoped lang="scss">
@import "@/assets/main.scss";

.experiment-comments {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .header-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 20px;
    padding-right: 58px;
  }

  h3 {
    margin: 0;
    color: $primary-color;
  }

  .meta-count {
    font-size: 0.92rem;
    font-weight: 500;
    color: rgba($primary-color, 0.8);
  }

  .add-comment-form {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .upload-controls {
      display: flex;
      align-items: center;
      justify-content: space-between; // Align items on opposite ends
      width: 100%; // Take full width to separate items

      .file-select-icon {
        cursor: pointer;
        color: $primary-color; 
        font-size: 2rem;
        &:hover {
          color: darken($primary-color, 10%);
        }
      }

      .cancel-icon {
        cursor: pointer;
        color: #e74c3c;
        &:hover {
          color: darken(#e74c3c, 10%);
        }
      }

      .submit-button-container {
        margin-left: auto; // Pushes the submit button to the right
        button {
          padding: 5px 10px;
          background-color: $primary-color;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          &:hover {
            background-color: darken($primary-color, 10%);
          }
        }
      }
    }
  }

  ul {
    list-style-type: none;
    padding: 0;
  }

  .comment-item {
    padding: 10px;
    margin-top: 10px;
    background: $form-color;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    border-radius: 4px;

    .comment-top {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .comment-meta {
        font-size: 0.85em;
        color: $primary-color;
      }

      .comment-actions {
        display: flex;
        gap: 5px;
      }
    }

    .comment-content {
      margin-top: 10px;
    }

    .edit-textarea, .comment-text, .comment-files {
      margin-top: 5px;
    }

    .button-group, .edit-btn, .remove-btn {
      padding: 5px 10px;
      border: none;
      cursor: pointer;
      border-radius: 4px;
      color: white;

    }
    .icon-btn {
      background: none;
      border: none;
      color: $primary-color;
      cursor: pointer;
      padding: 6px 8px;
      margin: 0 5px; 

      i {
        font-size: 1.5rem; 
      }

      &:hover {
        color: darken($primary-color, 20%);
      }
    }
    .save-btn i {
      color: $primary-color;
      &:hover {
        color: darken($primary-color, 20%);
      }
    }

    .cancel-btn i {
      color: #e74c3c;
      &:hover {
        color: darken(#e74c3c, 20%); 
      }
    }
  }
  .pagination {
    display: flex;
    justify-content: center;
    margin-top: 20px;

    button {
      margin: 0 5px;
      padding: 5px 10px;
      background-color: $primary-color;
      border: 1px solid $secondary-color;
      border-radius: 5px;
      cursor: pointer;

      &.active,
      &:hover {
        background-color: darken($primary-color, 10%);
      }
    }
  }
}
</style>
